import { useEffect, useRef } from 'react';

/**
 * useScrollReveal — IntersectionObserver-based scroll reveal.
 * Adds `.v2-revealed` to children with `.v2-reveal` when they enter the viewport.
 * Supports staggered delays via `data-reveal-delay` attribute.
 */
export default function useScrollReveal(options = {}) {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Respect reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      container.querySelectorAll('.v2-reveal').forEach(el => el.classList.add('v2-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const delay = Number(entry.target.dataset.revealDelay) || 0;
            if (delay > 0) {
              setTimeout(() => entry.target.classList.add('v2-revealed'), delay);
            } else {
              entry.target.classList.add('v2-revealed');
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: options.threshold || 0.12, rootMargin: options.rootMargin || '0px 0px -40px 0px' }
    );

    const elements = container.querySelectorAll('.v2-reveal');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [options.threshold, options.rootMargin]);

  return containerRef;
}
